import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { MessageService } from './message.service';
import { Observable, EMPTY } from 'rxjs';
import { Image } from '../models/image';
import { SynchronizationService } from './synchronization.service';
import { ConfigLoaderService } from '../config/configloader.service';
import { catchError } from 'rxjs/operators';
import { map } from 'rxjs/operators';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  })
};

/**
 * this class represents access to all rest requests concerning Gallery data of the PyWall
 * this rest service honors isMock from settingservice
 */
@Injectable()
export class GalleryService {

  private projectBaseURL: string;
  private baseURL: string;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private synchronizationService: SynchronizationService,
    private configService: ConfigLoaderService) {
    this.projectBaseURL = `${configService.settings.pyWallServer.url}projects`;
    this.baseURL = `${configService.settings.pyWallServer.url}`;
  }


  /**
   * Returns a set of gallery images of a project. The returned set is in a specified range ordered by a specified attribute
   * @param {String} projectKey the projectkey of the project
   * @param {Number} startIndex the start index of the range. First Element Included
   * @param {Number} endIndex the end index of the range. Last element Excluded
   * @param {String} orderBy by which attribute the set should be ordered
   * @returns {Observable<Image[]>}
   */
  getImages(projectKey: String, startIndex?: Number, endIndex?: Number, orderBy?: string, descending?: boolean): Observable<Image[]> {
    this.messageService.sendDebug(`GalleryService getImages called on project ${projectKey}`);

    let params = new HttpParams().append('projectId', String(projectKey));

    if (startIndex) {
      params = params.append('fromIndex', String(startIndex));
    }
    if (endIndex) {
      params = params.append('toIndex', String(endIndex));
    }
    if (orderBy) {
      params = params.append('orderBy', orderBy);
    }
    if (descending) {
      params = params.append('descending', String(descending));
    }

    const imagesUrl = this.baseURL + 'galleryimages';

    return this.http.get<Image[]>(imagesUrl, { params: params }).pipe(
      catchError((err: HttpErrorResponse) => EMPTY),
      map((results) => results['images'].map((imageData) =>
        new Image(imageData.id, imageData.name, this.baseURL + imageData.url, imageData.data)))
    );
  }

  /**
   * Adds a single gallery image to the backend.
   * @param {String} projectKey the projectkey of the project.
   * @param {String} image the image as base64-string which should be added to the backend.
   * @param {String} imageName The name of the image.
   * @param {any} onSuccess callback function which is called when the request was successful.
   * @param {any} onError callback function which is called when the request had an error.
   */
  addImage(projectKey: String, image: string, imageName: string, onSuccess: any, onError: any): void {
    this.messageService.sendDebug(`GalleryService getImage called on project ${projectKey}`);
    const imagesUrl = this.baseURL + 'galleryimages/';

    this.http.post<Image>(imagesUrl, {
      'image': image.split(',')[1],
      'name': imageName,
      'user': 'aWall',
      'projectId': projectKey
    }, httpOptions)
      .subscribe(img => {
        this.synchronizationService.sendPersistenceEvent('uploadImage', img);
        onSuccess(img);
      },
      error => {
        onError(error);
      }
      );
  }

  /**
   * Deletes a single gallery image in the backend.
   * @param {Number} image the image which should be deleted.
   * @param {any} onSuccess callback function which is called when the request was successful.
   * @param {any} onError callback function which is called when the request had an error.
   */
  deleteImage(image: Image, onSuccess: any, onError: any): void {
    this.messageService.sendDebug(`GalleryService delete image call on image-id ${image.id}`);
    const imagesUrl = this.baseURL + `galleryimages/${image.id}/`;
    this.http.delete(imagesUrl, {
      responseType: 'text',
    })
      .subscribe(
        response => {
          this.synchronizationService.sendPersistenceEvent('deleteImage', image);
          onSuccess(response);
        },
        error => {
          onError(error);
        }
      );
  }
}
